import { useMemo } from "react";
import {
    type Company,
    type EmissionRecordView,
    type Form,
    FormFieldsFieldTypeEnum,
} from "../../apiClient/generated";
import type { SuggestionConfig } from "./conditionEvaluator";

// All possible field types for form rendering
export type FormFieldType =
    | "emissionLinking" // Related emission dropdown
    | "user" // User assignment dropdown
    | "checkbox" // Standard checkbox
    | "radio" // Yes/No radio buttons (boolean fields with input_type=radio)
    | "site" // Site/equipment dropdown
    | "equipment" // Site/equipment dropdown
    | "datetime" // Combined date + time input
    | "date" // Date-only input
    | "textarea" // Multi-line text area
    | "text" // Single-line text input
    | "number" // Number input
    | "select" // Dropdown select with choices
    | "starRating" // 1-5 star rating
    | "fileUpload" // File upload component
    | "table" // Allows multiple child values, like a sub-table
    | "warning"; // Warning message, used for validation purposes only

export interface TableColumn {
    field_name: string;
    field_type:
        | "string"
        | "number"
        | "boolean"
        | "date"
        | "datetime"
        | "choice";
    label: string;
    choices?: string[];
}

// A single visibility condition - all conditions in the array must pass (AND logic)
export interface VisibilityCondition {
    field: string;
    is: "null" | "notNull" | "lt" | "gt" | "neq" | true | false;
    type?: "date"; // Convert values before comparing (e.g. ISO strings to Date timestamps)
    emissionField?: string; // Emission field to compare to
    stateField?: "currentlyOpenSite"; // Handled per case, current site page state
    // TODO: Add support for static values and event fields.
}

// Resolves a dot-notation path on an object (e.g. "dataPoint.detectionTimestamp")
const resolveNestedPath = (obj: Record<string, any>, path: string): any => {
    return path.split(".").reduce((current, key) => current?.[key], obj);
};

// A "required condition" object coming from the API may be present but empty
// (e.g. `{}`), which is truthy in JS but represents no actual condition.
// Treat it as no condition unless it has at least one own property.
const hasRequiredCondition = (cond: unknown): boolean =>
    cond != null &&
    typeof cond === "object" &&
    Object.keys(cond as Record<string, unknown>).length > 0;

// Evaluates visibleWhen conditions against current form values
export const evaluateVisibility = (
    visibleWhen: VisibilityCondition[] | undefined,
    formValues: Record<string, any>,
    emissionRecord: EmissionRecordView,
    states?: {
        currentlyOpenSite?: string;
    },
): boolean => {
    if (!visibleWhen) return true;
    return visibleWhen.every((c) => {
        let comparisonValue = null;
        if (c.emissionField) {
            comparisonValue = resolveNestedPath(
                emissionRecord as unknown as Record<string, any>,
                c.emissionField,
            );
        }
        if (c.stateField === "currentlyOpenSite") {
            comparisonValue = states?.currentlyOpenSite;
        }

        if (c.is === "null") {
            return formValues[c.field] === null;
        }
        if (c.is === "notNull") {
            return formValues[c.field] !== null;
        }
        if (c.is === "lt" || c.is === "gt") {
            const formVal = formValues[c.field];
            if (formVal == null || comparisonValue == null) return false;
            let cleanFormVal: number;
            let cleanComparisonValue: number;
            if (c.type === "date") {
                cleanFormVal = new Date(formVal).getTime();
                cleanComparisonValue = comparisonValue.getTime();
            }
            return c.is === "lt"
                ? cleanFormVal < cleanComparisonValue
                : cleanFormVal > cleanComparisonValue;
        }
        if (c.is === "neq") {
            return formValues[c.field] !== comparisonValue;
        }
        if (typeof c.is === "boolean") {
            return formValues[c.field] === c.is;
        }
        // Unsupported conditions evaluate to false
        // to avoid hiding fields.
        return false;
    });
};

export interface FieldDefinitionMetadata {
    emptyValue?: string;
    min?: number;
    max?: number;
    type?: string;
    message?: string;
    choices?: string[];
    columns?: TableColumn[];
    allowAny?: boolean;
    // When true: only renders for users with emissionEventApprove permission,
    // and filters the user list to approvers only.
    approverUsersOnly?: boolean;
}

// FieldDefinition defines a single form field
export interface FieldDefinition {
    fieldName: string | null;
    label: string;
    fieldType: FormFieldType;
    placeholder?: string;
    visibleWhen?: VisibilityCondition[];
    suggestionRule?: SuggestionConfig;
    metadata?: FieldDefinitionMetadata;
}

// SectionDefinition groups related fields
export interface SectionDefinition {
    groupName: string;
    fields: FieldDefinition[];
}

/**
 * useFormDefinition
 *
 * Builds the declarative form structure (sections and fields) based on
 * company settings and enabled custom forms. This is pure data describing
 * what fields exist - visibility logic lives in the field components.
 */
export const useFormDefinition = (
    company: Company | undefined,
    enabledForms: Form[],
) => {
    return useMemo((): SectionDefinition[] => {
        const sections: SectionDefinition[] = [
            {
                groupName: "Assignee, Site and Linking",
                fields: [
                    {
                        fieldName: "similarEmission",
                        label: "Similar emissions were found for this site, do you want to link them to already created events?",
                        fieldType: "emissionLinking",
                    },
                    {
                        fieldName: "responsible",
                        label: "Assign this event to a team member: *",
                        fieldType: "user",
                        visibleWhen: [{ field: "similarEmission", is: "null" }],
                    },
                    {
                        fieldName: "notifyResponsible",
                        label: "Notify team member via email",
                        fieldType: "checkbox",
                        visibleWhen: [
                            { field: "responsible", is: "notNull" },
                            { field: "similarEmission", is: "null" },
                        ],
                    },
                    ...(company?.settings?.approvalFlowEnabled
                        ? ([
                              {
                                  fieldName: "reviewer",
                                  label: "Coordinator for this event:",
                                  fieldType: "user",
                                  visibleWhen: [
                                      { field: "similarEmission", is: "null" },
                                  ],
                                  metadata: {
                                      approverUsersOnly: true,
                                  },
                              },
                          ] satisfies FieldDefinition[])
                        : []),
                    {
                        fieldName: "site",
                        label: "Associate this event with a site:",
                        fieldType: "site",
                        visibleWhen: [{ field: "similarEmission", is: "null" }],
                    },
                    {
                        fieldName: null,
                        label: "The site you selected is not the same being displayed in the page.",
                        fieldType: "warning",
                        visibleWhen: [
                            {
                                field: "site",
                                is: "neq",
                                stateField: "currentlyOpenSite",
                            },
                            { field: "site", is: "notNull" },
                            { field: "similarEmission", is: "null" },
                        ],
                    },
                    {
                        fieldName: "equipment",
                        label: "Associate with equipment at this site:",
                        fieldType: "equipment",
                        visibleWhen: [
                            { field: "site", is: "notNull" },
                            { field: "similarEmission", is: "null" },
                        ],
                    },
                ],
            },
        ];

        // Dynamic Form Data from enabled forms
        if (enabledForms && enabledForms.length > 0) {
            enabledForms.forEach((form) => {
                if (form.fields && form.fields.length > 0) {
                    const formSection: SectionDefinition = {
                        groupName: form.name,
                        fields: form.fields.map((field) => {
                            let fieldName: string | null =
                                `formData.${field.fieldName}`;
                            let fieldType: FormFieldType = "text";
                            if (
                                field.fieldType ===
                                FormFieldsFieldTypeEnum.Number
                            ) {
                                fieldType =
                                    field.fieldOptions?.input_type ===
                                    "star_rating"
                                        ? "starRating"
                                        : "number";
                            } else if (
                                field.fieldType ===
                                FormFieldsFieldTypeEnum.String
                            ) {
                                fieldType =
                                    field.fieldOptions?.text_type === "textarea"
                                        ? "textarea"
                                        : "text";
                            } else if (
                                field.fieldType ===
                                FormFieldsFieldTypeEnum.Choice
                            ) {
                                fieldType = "select";
                            } else if (
                                field.fieldType ===
                                FormFieldsFieldTypeEnum.Boolean
                            ) {
                                fieldType =
                                    field.fieldOptions?.input_type ===
                                    "checkbox"
                                        ? "checkbox"
                                        : "radio";
                            } else if (
                                field.fieldType ===
                                FormFieldsFieldTypeEnum.Datetime
                            ) {
                                fieldType = "datetime";
                            } else if (
                                field.fieldType === FormFieldsFieldTypeEnum.Date
                            ) {
                                fieldType = "date";
                            } else if (
                                field.fieldType === FormFieldsFieldTypeEnum.File
                            ) {
                                fieldType = "fileUpload";
                            } else if (
                                field.fieldType === FormFieldsFieldTypeEnum.User
                            ) {
                                fieldType = "user";
                            } else if (
                                field.fieldType ===
                                FormFieldsFieldTypeEnum.Table
                            ) {
                                fieldType = "table";
                            } else if (
                                field.fieldType ===
                                FormFieldsFieldTypeEnum.Warning
                            ) {
                                fieldType = "warning";
                                fieldName = null;
                            }

                            return {
                                fieldName,
                                label:
                                    field.label +
                                    (field.isRequired
                                        ? " *"
                                        : hasRequiredCondition(
                                                field.requiredCondition,
                                            )
                                          ? " **"
                                          : ""),
                                fieldType: fieldType,
                                visibleWhen: Array.isArray(field.displayWhen)
                                    ? field.displayWhen
                                    : undefined,
                                metadata: {
                                    choices:
                                        field.fieldOptions?.choices ||
                                        undefined,
                                    columns:
                                        field.fieldOptions?.columns ||
                                        undefined,
                                    allowAny: field.fieldOptions?.allow_any,
                                },
                                suggestionRule:
                                    field.suggestionRule as SuggestionConfig,
                            };
                        }),
                    };
                    sections.push(formSection);
                }
            });
        }

        sections.push({
            groupName: "Attachments",
            fields: [
                {
                    fieldName: null,
                    label: "Attachments:",
                    fieldType: "fileUpload",
                    visibleWhen: [
                        { field: "site", is: "notNull" },
                        { field: "similarEmission", is: "null" },
                    ],
                    metadata: {
                        message:
                            "File attachments are only available for accepted events.",
                    },
                },
            ],
        });

        sections.push({
            groupName: "Extra notes",
            fields: [
                {
                    fieldName: "extraNotes",
                    label: "Extra notes:",
                    fieldType: "textarea",
                    visibleWhen: [{ field: "similarEmission", is: "null" }],
                    metadata: {
                        message:
                            "Notes are only available for accepted events.",
                    },
                },
            ],
        });

        return sections;
    }, [company, enabledForms]);
};
